
import {
    StepperDropdownContainer,
    StepperDropdownContent,
    StepperDropdownHeader,
    StepperDropdownHeaderIcon,
    StepperDropdownHeaderTitle
} from '../../dropdown/StepperDropdown'
import { CloseSvg, StepCompletedSvg } from '../../../../../CommonComponents/svgs/Svgs'
import { FC, MouseEvent, useMemo, useState } from 'react'
import { StepperProps } from '../../../utils/stepper-types'
import useForm from '../../../hooks/useForm'
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip'
import { DEFAULT_ORBIT_COLOR } from '../../../../../../utils/constants'
import useStaticData from '../../../hooks/useStaticData'
import useModal from '../../../hooks/useModal'
import { showToastMessage } from '../../../../../../utils/commonFunctions'
import { validateSameFaults } from '../../../utils/validation'
import useScenarioResponse from '../../../hooks/useScenarioResponse'
import useViewer from '../../../hooks/useViewer'
import useFaultApis from '../../../hooks/useFaultApis'
import useNavigation from '../../../hooks/useNavigation'
import { CircularProgress } from '@mui/material'


const FaultInjection: FC<StepperProps> = ({ title, hideComponent, open, disabled, stepCompleted, onToggle }) => {
    const { missionId } = useNavigation()
    const { setModals } = useModal()
    const { updateGroundPackets } = useViewer()
    const { formData, setFormData } = useForm()
    const { getFaultsByGroupId } = useFaultApis()
    const { scenarioResponse } = useScenarioResponse()
    const { allFaults, setScenarioBaseData } = useStaticData()
    const [loading, setLoading] = useState<boolean | string>(false)

    const selectedSatellites = useMemo(() => formData.satellites, [formData.satellites])
    const scenarioDeploymentType = useMemo(() => formData.details.scenarioDeploymentType, [formData.details.scenarioDeploymentType])
    const selectedFaults = useMemo(() => formData.faults || [], [formData.faults])

    const resetSelectionModal = () => {
        setModals((prev) => ({
            ...prev,
            configured: null
        }))
        return showToastMessage('info', 'No Faults present for this Satellite Group')
    }

    const getFaults = async (groupId: string) => {
        setLoading(groupId)
        try {
            const groupFaults = await getFaultsByGroupId(missionId, groupId)
            setScenarioBaseData((prev) => {
                return {
                    ...prev,
                    allFaults: {
                        ...prev.allFaults,
                        [groupId]: groupFaults || []
                    }
                }
            })
            if (!groupFaults.length) {
                return resetSelectionModal()
            }
            setModals((prev) => ({
                ...prev,
                configured: {
                    open: true,
                    name: 'fault',
                    data: {
                        allFaults: groupFaults,
                        selectedFault: null
                    }
                }
            }))
        } catch (error) {
            return resetSelectionModal()
        } finally {
            setLoading(false)
        }
    }

    const openFaultSelectionModal = async (groupId: string) => {
        if (!allFaults[groupId]) {
            await getFaults(groupId)
        } else if (allFaults[groupId]?.length === 0) {
            return resetSelectionModal()
        } else {
            setModals((prev) => ({
                ...prev,
                configured: {
                    open: true,
                    name: 'fault',
                    data: {
                        allFaults: allFaults[groupId],
                        selectedFault: null
                    }
                }
            }))
        }
    }

    const removeFault = (groupId: string) => {
        setFormData((prev) => {
            const faults = prev.faults?.filter((fault) => fault?.satelliteGroupId !== groupId)
            const status = prev.assets.faultPlans.data ? validateSameFaults(faults, scenarioResponse!) ? 'completed' : 'invalidated' : prev.assets.faultPlans.status
            const data = {
                ...prev,
                faults: faults,
                assets: {
                    ...prev?.assets,
                    faultPlans: {
                        ...prev?.assets?.faultPlans,
                        status: status
                    }
                }
            }
            updateGroundPackets(data)
            return data
        })
    }

    if (hideComponent) return null
    return (
        <StepperDropdownContainer
            open={open}
            disabled={disabled}
        >
            <StepperDropdownHeader
                title={title}
                onToggle={onToggle}
            >
                <StepperDropdownHeaderTitle>
                    {title}
                </StepperDropdownHeaderTitle>
                <StepperDropdownHeaderIcon show={stepCompleted}>
                    <StepCompletedSvg />
                </StepperDropdownHeaderIcon>
            </StepperDropdownHeader>
            <StepperDropdownContent>
                <div className='form__input__container vertical__align'>
                    {selectedSatellites.map((satellite) => {
                        const satelliteName = scenarioDeploymentType === 'HARDWARE_IN_LOOP' ? satellite.satelliteName : satellite.groupName
                        const faultPlanName = selectedFaults.find((fault) => fault?.satelliteGroupId === satellite?.groupId)?.faultPlanName
                        const buttonText = loading === satellite?.groupId ? 'Fetching' : faultPlanName || 'No Fault Plan'
                        const showLoading = loading === satellite?.groupId
                        return (
                            <div key={satellite.groupId} className='form__field__card'>
                                <div className='Form__card__details__container'>
                                    <div className='form__field__card__color__box' style={{ background: (satellite?.color !== '' && !satellite?.color.includes('#')) ? `rgba(${JSON.parse(satellite?.color).join(",")})` : `rgba(${DEFAULT_ORBIT_COLOR.join(",")})` }} />
                                    <div className='form__field__card__text'>
                                        <CustomToolTip title={satelliteName}>
                                            {satelliteName}
                                        </CustomToolTip>
                                    </div>
                                </div>
                                <button
                                    disabled={loading ? true : false}
                                    className={`form__field__card__button modal__trigger__button ${faultPlanName && 'active'}`}
                                    onClick={() => {
                                        if (faultPlanName) return
                                        openFaultSelectionModal(satellite?.groupId)
                                    }}
                                >
                                    <div className='card__button__text'>
                                        <CustomToolTip title={faultPlanName || ''}>
                                            <div className='d-flex align-items-center gap-2 w-100'>
                                                <span className='text-truncate w-100'>
                                                    {buttonText}
                                                </span>
                                                {showLoading && <CircularProgress color='inherit' size={14} />}
                                            </div>
                                        </CustomToolTip>
                                    </div>
                                    {faultPlanName &&
                                        <CustomToolTip title='Remove'>
                                            <button className='card__button__icon'
                                                onClick={(event: MouseEvent<HTMLButtonElement>) => {
                                                    event.stopPropagation()
                                                    removeFault(satellite?.groupId)
                                                }}
                                            >
                                                <CloseSvg />
                                            </button>
                                        </CustomToolTip>
                                    }
                                </button>
                            </div>
                        )
                    })}
                </div>
            </StepperDropdownContent>
        </StepperDropdownContainer>
    )
}
export default FaultInjection